import type React from 'react'
import { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import { io } from 'socket.io-client'
import { SOCKET_API_PATH, SOCKET_AUTHENTICATION_ERROR_MESSAGES } from './constants.ts'
import type { ReactSocket, ServerToReactMessages } from './types.ts'

type Listener = (params: ServerToReactMessages) => void

interface SocketContextType {
  socket: ReactSocket | null
  subscribe: (listener: Listener) => () => void
}

const SocketContext = createContext<SocketContextType>({
  socket: null,
  subscribe: () => () => undefined,
})

export type SocketProps = {
  onMessage: (params: ServerToReactMessages) => void
}

type ConnectErrorHandler = (error: Error) => void

export const SocketProvider = ({
  selectApiUrl,
  getToken,
  isAuthenticated,
  isSocketServerDisabled,
  connectionKey,
  onConnectError,
  children,
}: {
  getToken: (() => Promise<string | null>) | null
  selectApiUrl: string
  isAuthenticated: boolean
  isSocketServerDisabled: boolean
  /**
   * Changing this value forces the socket to reconnect with fresh auth.
   */
  connectionKey?: string | number | null
  onConnectError?: ConnectErrorHandler
  children: React.ReactNode
}) => {
  const listenersRef = useRef<Set<Listener>>(new Set())
  const socket = useRef<ReactSocket | null>(null)
  const onConnectErrorRef = useRef(onConnectError)

  useEffect(() => {
    onConnectErrorRef.current = onConnectError
  }, [onConnectError])

  useEffect(() => {
    // This value intentionally participates in the effect lifecycle only.
    // When it changes, React runs the cleanup below and opens a fresh socket.
    void connectionKey

    if (isSocketServerDisabled || !isAuthenticated || !getToken) {
      socket.current?.disconnect()
      socket.current = null
      return
    }

    const nextSocket = io(selectApiUrl, {
      path: SOCKET_API_PATH,
      withCredentials: true,
      reconnection: true,
      reconnectionAttempts: Number.POSITIVE_INFINITY,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      transports: ['websocket'],
      auth: async data =>
        data({
          token: await getToken(),
        }),
    })

    socket.current = nextSocket

    nextSocket.on('message', payload => {
      for (const listener of listenersRef.current) {
        listener(payload)
      }
    })

    nextSocket.on('connect_error', err => {
      onConnectErrorRef.current?.(err)

      switch (err.message) {
        case SOCKET_AUTHENTICATION_ERROR_MESSAGES.invalidToken:
        case SOCKET_AUTHENTICATION_ERROR_MESSAGES.unauthenticated:
          // @todo - re-authentication strategy
          break
      }
    })

    return () => {
      nextSocket.disconnect()
      if (socket.current === nextSocket) {
        socket.current = null
      }
    }
  }, [getToken, selectApiUrl, isAuthenticated, isSocketServerDisabled, connectionKey])

  const subscribe = useCallback((listener: Listener) => {
    listenersRef.current.add(listener)
    return () => {
      listenersRef.current.delete(listener)
    }
  }, [])

  const contextValue = useMemo(
    () => ({
      socket: socket.current,
      subscribe,
    }),
    [subscribe],
  )

  return <SocketContext.Provider value={contextValue}>{children}</SocketContext.Provider>
}

export const useSocket = ({ onMessage }: SocketProps): ReactSocket | null => {
  const { socket, subscribe } = useContext(SocketContext)

  useEffect(() => {
    return subscribe(onMessage)
  }, [subscribe, onMessage])

  return socket
}
